import React, { useEffect, useMemo, useRef, useState } from "react";

/** Close dropdown on outside click */
function useClickAway(ref, onAway) {
  useEffect(() => {
    function onDoc(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) onAway?.();
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [ref, onAway]);
}

function Menu({ open, children }) {
  if (!open) return null;
  return (
    <ul
      className="dropdown-menu show"
      style={{ position: "absolute", inset: "auto 0 0 0", transform: "translateY(100%)" }}
    >
      {children}
    </ul>
  );
}

/** DESCRIPTION TYPEAHEAD — pick a single value */
export function DescriptionTypeaheadInput({
  value,
  onChange,                 // (next: string)
  suggestions = [],         // string[]
  placeholder = "",
  maxMatches = 3,
  minChars = 2,
}) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value || "");
  const [hover, setHover] = useState(-1);

  useEffect(() => setQ(value || ""), [value]);
  useClickAway(wrapRef, () => setOpen(false));

  const list = useMemo(() => {
    const s = (q || "").trim().toLowerCase();
    if (s.length < minChars) return [];
    const seen = new Set();
    const matches = [];
    for (const d of suggestions) {
      if (!d) continue;
      const dl = d.toLowerCase();
      if (dl.includes(s) || dl.startsWith(s)) {
        if (!seen.has(dl)) { seen.add(dl); matches.push(d); }
      }
      if (matches.length >= maxMatches) break;
    }
    return matches;
  }, [q, suggestions, maxMatches, minChars]);

  const commit = (pick) => {
    onChange?.(pick);
    setQ(pick);
    setOpen(false);
  };

  const onInput = (e) => {
    const next = e.target.value;
    setQ(next);
    onChange?.(next);
    setOpen(true);
  };

  const onKeyDown = (e) => {
    if (!list.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHover((h) => Math.min(list.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHover((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter" || e.key === "Tab") {
      if (hover >= 0) { e.preventDefault(); commit(list[hover]); }
    }
  };

  return (
    <div className="position-relative" ref={wrapRef}>
      <input
        className="form-control"
        value={q}
        onChange={onInput}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
      />
      <Menu open={open && list.length}>
        {list.map((s, i) => (
          <li key={s}>
            <button
              type="button"
              className={`dropdown-item ${i === hover ? "active" : ""}`}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(-1)}
              onClick={() => commit(s)}
              title={s}
            >
              {s}
            </button>
          </li>
        ))}
      </Menu>
    </div>
  );
}

/** TAGS MULTI-TYPEAHEAD — adds "tag, " and re-arms; defaults to 5 suggestions */
export function TagsMultiTypeaheadInput({
  value,
  onChange,                  // (next: string)
  suggestions = [],          // string[]
  placeholder = "Comma-separated…",
  maxMatches = 5,            // ← trimmed to 5 by default
  minChars = 1,
}) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value || "");
  const [hover, setHover] = useState(-1);
  useEffect(() => setQ(value || ""), [value]);
  useClickAway(wrapRef, () => setOpen(false));

  const parts = useMemo(() => q.split(",").map(s => s.trim()).filter(Boolean), [q]);
  const currentTerm = useMemo(() => {
    const idx = q.lastIndexOf(",");
    return (idx === -1 ? q : q.slice(idx + 1)).trim();
  }, [q]);

  const list = useMemo(() => {
    const term = (currentTerm || "").toLowerCase();
    if (term.length < minChars) return [];
    const chosen = new Set(parts.map(p => p.toLowerCase()));
    const matches = [];
    for (const t of suggestions) {
      const tl = String(t || "").toLowerCase();
      if (chosen.has(tl)) continue;
      if (tl.includes(term) || tl.startsWith(term)) matches.push(t);
      if (matches.length >= maxMatches) break; // ← hard cap
    }
    return matches;
  }, [currentTerm, suggestions, parts, maxMatches, minChars]);

  // FIX: replace the last typed segment with the picked tag (don’t append it after the unfinished term)
  const commitAdd = (pick) => {
    const lastComma = q.lastIndexOf(",");
    const baseParts =
      lastComma >= 0
        ? parts.slice(0, -1) // drop the current (partial) segment
        : [];                // no comma yet → nothing chosen before
    // dedupe (case-insensitive)
    const lower = new Set(baseParts.map((p) => p.toLowerCase()));
    if (!lower.has(String(pick).toLowerCase())) baseParts.push(pick);

    const next = (baseParts.length ? baseParts.join(", ") + ", " : pick + ", ");
    setQ(next);
    onChange?.(next);
    setOpen(true); // keep open for next tag
    setHover(-1);
  };

  const onInput = (e) => {
    const next = e.target.value;
    setQ(next);
    onChange?.(next);
    setOpen(true);
  };

  const onKeyDown = (e) => {
    if (!list.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHover((h) => Math.min(list.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHover((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter" || e.key === "Tab") {
      if (hover >= 0) { e.preventDefault(); commitAdd(list[hover]); }
    }
  };

  return (
    <div className="position-relative" ref={wrapRef}>
      <input
        className="form-control"
        value={q}
        onChange={onInput}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
      />
      <Menu open={open && list.length}>
        {list.map((s, i) => (
          <li key={s}>
            <button
              type="button"
              className={`dropdown-item ${i === hover ? "active" : ""}`}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(-1)}
              onClick={() => commitAdd(s)}
              title={s}
            >
              {s}
            </button>
          </li>
        ))}
      </Menu>
    </div>
  );
}
